import { useEffect } from 'react'
import { getStytchRedirectURL } from '@/utilities/getDomain'

export const SKIP_GOOGLE_ONE_TAP_STORAGE_KEY = 'ash.skip-google-one-tap'

type GoogleAccountsId = {
  cancel?: () => void
  disableAutoSelect?: () => void
}

export type GoogleOneTapStytchClient = {
  organization?: {
    getBySlug?: (data: { organization_slug: string }) => Promise<{
      organization?: { organization_id?: string } | null
    }>
  }
  oauth?: {
    googleOneTap?: {
      start?: (options: {
        organization_id: string
        login_redirect_url: string
        signup_redirect_url: string
        cancel_on_tap_outside?: boolean
      }) => Promise<unknown>
      discovery?: {
        start?: (options: {
          discovery_redirect_url: string
          cancel_on_tap_outside?: boolean
        }) => Promise<unknown>
      }
    }
  }
}

let googleOneTapStartPromise: Promise<void> | null = null

export function markGoogleOneTapSkippedAfterLogout(): void {
  if (typeof sessionStorage === 'undefined') {
    return
  }
  sessionStorage.setItem(SKIP_GOOGLE_ONE_TAP_STORAGE_KEY, '1')
}

export function shouldSkipGoogleOneTap(reason?: string | null): boolean {
  if (reason === 'session-expired') {
    return false
  }
  if (reason === 'logout') {
    return true
  }
  try {
    return sessionStorage.getItem(SKIP_GOOGLE_ONE_TAP_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

export function clearGoogleOneTapSkip(): void {
  if (typeof sessionStorage === 'undefined') {
    return
  }
  sessionStorage.removeItem(SKIP_GOOGLE_ONE_TAP_STORAGE_KEY)
}

/** Google's post-logout guidance: cancel an in-flight prompt and disable auto-select. */
export function dismissGoogleOneTap(): void {
  if (typeof window === 'undefined') {
    return
  }
  const googleId = (window as Window & { google?: { accounts?: { id?: GoogleAccountsId } } }).google
    ?.accounts?.id
  try {
    googleId?.cancel?.()
    googleId?.disableAutoSelect?.()
  } catch {
    // GSI is not always present on /logout.
  }
}

export function resetGoogleOneTapStart(): void {
  googleOneTapStartPromise = null
}

/**
 * Stytch's widget One Tap remounts with organization state and React Strict Mode,
 * which starts a second FedCM credentials.get. Start One Tap once through the
 * headless client instead, and keep the widget Google button on one_tap: false.
 */
export function startStytchGoogleOneTap({
  stytchClient,
  organizationSlug,
}: {
  stytchClient: GoogleOneTapStytchClient
  organizationSlug?: string | null
}): Promise<void> {
  if (googleOneTapStartPromise !== null) {
    return googleOneTapStartPromise
  }

  const oneTap = stytchClient.oauth?.googleOneTap
  if (!oneTap?.start && !oneTap?.discovery?.start) {
    return Promise.resolve()
  }

  googleOneTapStartPromise = (async () => {
    const redirectURL = getStytchRedirectURL()

    if (organizationSlug && oneTap?.start && stytchClient.organization?.getBySlug) {
      try {
        const org = await stytchClient.organization.getBySlug({
          organization_slug: organizationSlug,
        })
        const organizationId = org.organization?.organization_id
        if (organizationId) {
          await oneTap.start({
            organization_id: organizationId,
            login_redirect_url: redirectURL,
            signup_redirect_url: redirectURL,
            cancel_on_tap_outside: true,
          })
          return
        }
      } catch {
        // Unknown slug should still try discovery One Tap.
      }
    }

    await oneTap.discovery?.start?.({
      discovery_redirect_url: redirectURL,
      cancel_on_tap_outside: true,
    })
  })()

  return googleOneTapStartPromise
}

export function useStytchGoogleOneTap({
  enabled,
  organizationSlug,
  stytchClient,
}: {
  enabled: boolean
  organizationSlug?: string | null
  stytchClient?: GoogleOneTapStytchClient | null
}): void {
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- headless One Tap starts when the login page is ready, not from a user event
    if (!enabled || !stytchClient) {
      return
    }

    void startStytchGoogleOneTap({
      stytchClient,
      organizationSlug,
    }).catch(() => {
      // Stytch logs the FedCM failure. Telemetry watches for the prompt iframe.
    })
  }, [enabled, organizationSlug, stytchClient])
}
